// Step 5 of the import wizard: the one button that brings the vendors in, and
// everything after it: the result, a commit written in the background and
// followed on the count report, and a commit that stopped part way.
//
// The shell imports this file first, so the stylesheets these components bring
// reach the page in the order they had before the split (checked once, by
// comparing the built CSS before and after; no test pins it).
//
// Release 3.3 (I5; plan 4.6): step 5 is one summary line of figures and the
// one promise line; the background progress says "412 of 773" and that
// leaving is safe; the result has one primary button (View vendors), a More
// menu for the rest, and the undo date as plain text beside its button.

import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { ImportDocuments } from '../../components/ImportDocuments'
import { CountReportButton, ImportError, UndoForm, UndoSaid } from '../../components/ImportUndo'
import './result.css'
import { Icon } from '../../components/ui'
import { ReasonTip } from '../../components/vendor/ReasonTip'
import {
  ApiError,
  api,
  refusalReason,
  shortDate,
  type CommitResult,
  type ImportCounts,
  type ImportReport,
  type ImportUndoResult,
} from '../../lib/api'
import { IMPORT_PROMISE, importingWords, plural, stoppedWords } from '../../lib/importWords'
import { addLabel } from './shared'

export function ConfirmPanel({
  counts,
  busy,
  error,
  allowed,
  refusal,
  onBack,
  onCommit,
}: {
  counts: ImportCounts
  busy: boolean
  error: unknown
  allowed: boolean
  refusal: string
  onBack: () => void
  onCommit: () => void
}) {
  return (
    <section className="panel" aria-labelledby="imw-go-h">
      <h2 id="imw-go-h" style={{ margin: 0 }}>
        Ready to import
      </h2>
      <ul className="imw-figs">
        {confirmFigures(counts).map((f) => (
          <li key={f.words}>
            <b>{f.n.toLocaleString('en-IN')}</b> {f.words}
          </li>
        ))}
      </ul>
      <p className="note imw-banner">
        <Icon name="shield" size={18} />
        <span>{IMPORT_PROMISE}</span>
      </p>
      <ImportError error={error} />
      <div className="imw-step-foot">
        <button type="button" onClick={onBack} disabled={busy}>
          Back
        </button>
        <button
          type="button"
          className="primary big imw-push"
          data-action="tprm.import.a02"
          disabled={busy || !allowed || counts.add + counts.update === 0}
          title={allowed ? undefined : refusal}
          onClick={onCommit}
        >
          {busy ? 'Importing...' : addLabel(counts)}
        </button>
        {!allowed && (
          <p className="imw-why-not sw-row">
            <span>You cannot import vendors.</span>
            <ReasonTip label="Why you cannot import vendors" text={refusal} />
          </p>
        )}
      </div>
    </section>
  )
}

// Step 5's one line of figures (plan 4.6): what will be added, updated,
// skipped as already on the register, and not imported yet. A figure of 0 is
// left out, except what will be added, which is the step's subject.
export function confirmFigures(c: ImportCounts): { n: number; words: string }[] {
  const notIn = c.attention + c.held
  const out = [{ n: c.add, words: (c.add === 1 ? 'vendor' : 'vendors') + ' will be added' }]
  if (c.update > 0) out.push({ n: c.update, words: 'will be updated' })
  if (c.leftAlone > 0) out.push({ n: c.leftAlone, words: 'skipped, already on the register' })
  if (notIn > 0) out.push({ n: notIn, words: 'not imported yet (to fix or on hold)' })
  return out
}

export function resultHeadline(r: CommitResult): string {
  const fix = (r.held ?? 0) + (r.attention ?? 0)
  const parts = [plural(r.committed, 'vendor') + ' added']
  if ((r.updated ?? 0) > 0) parts.push((r.updated ?? 0).toLocaleString('en-IN') + ' updated')
  if (fix > 0) parts.push(fix.toLocaleString('en-IN') + ' to fix')
  return parts.join('. ') + '.'
}

// "More": a button that opens a short list under it (the vendor page's More
// is the same pattern). The list stays in the page while closed, hidden, so a
// download started from it keeps its state; a press outside or Escape closes
// it, and Escape gives the focus back to the button. So does a choice that
// closes it: the item pressed is hidden with the list, and the focus would
// be left on nothing (I5 review nit, 13 Oct 2026).
export function MoreMenu({ label, children }: { label: string; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const id = useId()
  const chosen = () => {
    setOpen(false)
    root.current?.querySelector<HTMLButtonElement>('button')?.focus()
  }
  useEffect(() => {
    if (!open) return
    const away = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        root.current?.querySelector<HTMLButtonElement>('button')?.focus()
      }
    }
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', key)
    }
  }, [open])
  return (
    <div className="imw-more" ref={root}>
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>
        {label}
      </button>
      <ul className="imw-more-list" id={id} hidden={!open}>
        {children(chosen)}
      </ul>
    </div>
  )
}

// Undo and attaching documents are refused (403) to a person without
// vendor.create even when they hold vendor.import (the founder's rule for
// 3.2: a steward prepares an import, a role that may create vendors imports it
// and takes it back out). Both buttons stay on the page, disabled, with the
// same refusal the API gives.
export function ResultPanel({
  result,
  importId,
  mayCreate,
  onAnother,
}: {
  result: CommitResult
  importId: string
  mayCreate: boolean
  onAnother: () => void
}) {
  const refusal = mayCreate ? undefined : refusalReason('vendor.create')
  const [panel, setPanel] = useState<'' | 'undo' | 'docs'>('')
  const [undone, setUndone] = useState<ImportUndoResult | null>(null)
  const held = (result.held ?? 0) + (result.attention ?? 0)
  const extras: string[] = []
  if ((result.leftAlone ?? 0) > 0) extras.push(plural(result.leftAlone, 'vendor') + ' skipped (already in the register).')
  if ((result.contacts ?? 0) > 0) extras.push(plural(result.contacts ?? 0, 'contact') + ' added.')
  if ((result.claims ?? 0) > 0)
    extras.push(plural(result.claims ?? 0, 'certificate or agreement', 'certificates and agreements') + ' noted, with no file yet.')
  if ((result.extraFields ?? 0) > 0) extras.push(plural(result.extraFields ?? 0, 'column') + ' kept as extra fields.')
  if ((result.startingTiers ?? 0) > 0)
    extras.push(plural(result.startingTiers ?? 0, 'tier needs', 'tiers need') + ' approval. Vendors keep the calculated tier until then.')
  if ((result.ownersUnmatched ?? 0) > 0)
    extras.push(plural(result.ownersUnmatched ?? 0, 'owner') + ' could not be matched to a person. You can match them later.')
  if ((result.keptForApproval ?? 0) > 0)
    extras.push(plural(result.keptForApproval ?? 0, 'vendor') + ' kept its business owner for now, because an approval needing it is in progress. The report says which.')
  const failures = result.failures ?? []

  return (
    <section className="panel p1-sent" aria-labelledby="imw-done-h">
      <div className="sent-hero">
        <span className="check-pop">
          <Icon name="check" size={26} />
        </span>
        <h2 id="imw-done-h" className="imw-headline">
          {resultHeadline(result)}
        </h2>
      </div>
      {extras.length > 0 && <p className="muted imw-rdetail">{extras.join(' ')}</p>}
      {result.failed > 0 && (
        <div className="note warn" style={{ marginBottom: 10 }}>
          <strong>{plural(result.failed, 'vendor')} could not be added</strong>
          {failures.length > 0 && (
            <ul className="p1-inline-list">
              {failures.slice(0, 8).map((f, i) => (
                <li key={i}>
                  Row {f.sourceRow}: {f.detail}
                </li>
              ))}
              {failures.length > 8 && <li>and {failures.length - 8} more, listed in the report</li>}
            </ul>
          )}
        </div>
      )}
      <div className="p1-actions imw-ractions">
        <a className="button primary" href="#/vendors">
          View vendors
        </a>
        <MoreMenu label="More">
          {(close) => (
            <>
              <li>
                <CountReportButton importId={importId} />
              </li>
              {!undone && (
                <li>
                  <button
                    type="button"
                    disabled={!mayCreate}
                    title={refusal}
                    onClick={() => {
                      close()
                      setPanel('docs')
                    }}
                  >
                    Add contracts and MSAs
                  </button>
                </li>
              )}
              <li>
                <button
                  type="button"
                  onClick={() => {
                    close()
                    onAnother()
                  }}
                >
                  Import another file
                </button>
              </li>
              {held > 0 && (
                <li>
                  <a href="#/vendor/new" title="The report lists the vendors held back and why">
                    {held === 1 ? 'Add the held vendor by hand' : 'Add a held vendor by hand'}
                  </a>
                </li>
              )}
            </>
          )}
        </MoreMenu>
      </div>

      {!undone && (
        <p className="imw-undo-line">
          {result.undoUntil && <span>You can undo this until {shortDate(result.undoUntil)}.</span>}
          <button
            type="button"
            className="linkish"
            aria-expanded={panel === 'undo'}
            disabled={!mayCreate}
            title={refusal}
            onClick={() => setPanel(panel === 'undo' ? '' : 'undo')}
          >
            Undo this import
          </button>
          {!mayCreate && <ReasonTip label="Why you cannot undo it or add its documents" text={refusal} />}
        </p>
      )}

      {panel === 'undo' && !undone && mayCreate && (
        <UndoForm
          importId={importId}
          undoUntil={result.undoUntil}
          onDone={(r) => {
            setUndone(r)
            setPanel('')
          }}
          onCancel={() => setPanel('')}
        />
      )}
      {undone && <UndoSaid result={undone} undoUntil={result.undoUntil} />}
      {panel === 'docs' && !undone && mayCreate && <ImportDocuments importId={importId} onClose={() => setPanel('')} />}
    </section>
  )
}

// ------------------------------------------- a commit in the background

// How often the page reads the count report while a commit runs.
export const FOLLOW_MS = 3000

export type CommitFollow = {
  progress: (p: { done: number; total: number }) => void
  finished: (r: CommitResult) => void
  stopped: (r: ImportReport) => void
  // A read that failed. ended: it was a refusal, and the following stopped.
  failed: (err: unknown, ended: boolean) => void
}

// The result page's counts from the count report, for a commit that finished
// without leaving its own answer on the report.
export function resultFromReport(r: ImportReport): CommitResult {
  return {
    importId: r.importId,
    status: r.status || 'COMMITTED',
    committed: r.created ?? 0,
    updated: r.updated ?? 0,
    leftAlone: r.skipped ?? 0,
    held: r.held ?? 0,
    attention: r.rejected ?? 0,
    failed: 0,
    undoUntil: r.undoUntil,
  }
}

// Follows a commit the API writes in the background (it answered 202): reads
// the count report every `every` ms, one read at a time, until the report
// carries the commit's result (finished), or says the commit stopped part way
// with nobody writing it (stopped). A read that fails is said and tried again,
// except a refusal (4xx), which ends the following. Returns the function that
// stops it; the page calls it when it goes away, so nothing is read after.
export function followCommit(importId: string, on: CommitFollow, every = FOLLOW_MS): () => void {
  let live = true
  let timer: ReturnType<typeof setTimeout> | undefined
  const next = () => {
    if (live) timer = setTimeout(() => void tick(), every)
  }
  async function tick() {
    if (!live) return
    let r: ImportReport
    try {
      r = await api.importReport(importId)
    } catch (err) {
      if (!live) return
      const refused = err instanceof ApiError && err.status >= 400 && err.status < 500
      if (refused) live = false
      on.failed(err, refused)
      next()
      return
    }
    if (!live) return
    if (r.result) {
      live = false
      on.finished(r.result)
    } else if (r.interrupted) {
      live = false
      on.stopped(r)
    } else if (r.status && r.status !== 'COMMITTING') {
      live = false
      on.finished(resultFromReport(r))
    } else {
      if (r.progress) on.progress(r.progress)
      next()
    }
  }
  next()
  return () => {
    live = false
    if (timer !== undefined) clearTimeout(timer)
  }
}

// While the rows are written in the background.
export function CommitProgress({ progress, error }: { progress: { done: number; total: number } | null; error: unknown }) {
  const total = Math.max(0, progress?.total ?? 0)
  const done = Math.min(Math.max(0, progress?.done ?? 0), total)
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  return (
    <section className="panel" aria-labelledby="imw-going-h" aria-busy="true">
      <h2 id="imw-going-h" className="imw-headline" style={{ margin: '0 0 10px' }}>
        {importingWords(total)}
      </h2>
      <div
        className="imw-progress"
        role="progressbar"
        aria-labelledby="imw-going-h"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
      >
        <span style={{ width: pct + '%' }} />
      </div>
      <p className="imw-count" role="status">
        <b>{done.toLocaleString('en-IN')}</b> of {total.toLocaleString('en-IN')} imported so far.
      </p>
      <p className="imw-small" style={{ margin: '0 0 10px' }}>
        You can leave this page; it continues. Find it in <a href="#/imports">Import history</a>.
      </p>
      <ImportError error={error} />
    </section>
  )
}

// A commit that stopped part way, with nobody writing it any more: how far it
// got, Resume (the same commit again, which picks up where it stopped) and
// Undo what came in. Both need vendor.create, like the commit and the undo, so
// they are shown disabled with the API's own refusal to anybody without it.
export function CommitStopped({
  report,
  importId,
  mayCreate,
  busy,
  error,
  onCarryOn,
  onAnother,
}: {
  report: ImportReport
  importId: string
  mayCreate: boolean
  busy: boolean
  error: unknown
  onCarryOn: () => void
  onAnother: () => void
}) {
  const refusal = mayCreate ? undefined : refusalReason('vendor.create')
  const [undoing, setUndoing] = useState(false)
  const [undone, setUndone] = useState<ImportUndoResult | null>(null)
  const total = report.progress?.total ?? 0
  const done = report.progress?.done ?? 0
  return (
    <section className="panel" aria-labelledby="imw-stopped-h">
      <h2 id="imw-stopped-h" style={{ margin: '0 0 6px' }}>
        {stoppedWords(done, total)}
      </h2>
      {!undone && (
        <p className="imw-lead">
          Resume or undo. Nothing that came in is lost.
        </p>
      )}
      <ImportError error={error} />
      {!undone && (
        <div className="p1-actions">
          {report.canResume !== false && (
            <button type="button" className="primary" disabled={busy || !mayCreate} title={refusal} onClick={onCarryOn}>
              {busy ? 'Resuming...' : 'Resume'}
            </button>
          )}
          {report.canUndo !== false && (
            <button
              type="button"
              aria-expanded={undoing}
              disabled={busy || !mayCreate}
              title={refusal}
              onClick={() => setUndoing(!undoing)}
            >
              Undo what came in
            </button>
          )}
          <CountReportButton importId={importId} />
          {!mayCreate && <ReasonTip label="Why you cannot resume or undo it" text={refusal} />}
        </div>
      )}
      {undoing && !undone && mayCreate && (
        <UndoForm
          importId={importId}
          undoUntil={report.undoUntil}
          onDone={(r) => {
            setUndone(r)
            setUndoing(false)
          }}
          onCancel={() => setUndoing(false)}
        />
      )}
      {undone && (
        <>
          <UndoSaid result={undone} undoUntil={report.undoUntil} />
          <div className="p1-actions" style={{ marginTop: 12 }}>
            <button type="button" onClick={onAnother}>
              Import another file
            </button>
          </div>
        </>
      )}
    </section>
  )
}
